import { Star } from "lucide-react";
import { Button } from "snap-design-system";
import { cn } from "~/lib/utils";
import { useFavoriteCRUD } from "~/hooks/useFavoriteCRUD";
import { useFavoriteStatus } from "~/store/favoritesStore";
import type { ResourceType } from "~/services/gateways/favorite.gateway";

interface FavoriteButtonProps {
  resourceType: ResourceType;
  resourceId: string;
  isFavorited: boolean;
  className?: string;
  /** Icon size in pixels. Defaults to 20. */
  size?: number;
  /**
   * QA selector prefix. The rendered attribute is `{prefix}-favorite-button` when the
   * resource is not yet favorited and `{prefix}-unfavorite-button` when it is, so the
   * selector always names the action the click performs.
   */
  testIdPrefix?: string;
}

/**
 * Reusable toggle button that allows users to mark reports or folders as favorites.
 * Renders a star icon — filled when favorited, outline when not.
 *
 * The displayed state is derived from the favorites store (optimistic override)
 * merged with the `isFavorited` base prop, so toggles reflect instantly across
 * every place the resource is shown without re-querying the list.
 */
export function FavoriteButton({
  resourceType,
  resourceId,
  isFavorited,
  className,
  size = 20,
  testIdPrefix,
}: FavoriteButtonProps) {
  const { toggleFavoriteMutation } = useFavoriteCRUD();
  const effectiveFavorited = useFavoriteStatus(resourceId, isFavorited);
  const isPending = toggleFavoriteMutation.isPending;

  const toggle = () => {
    if (isPending) return;
    toggleFavoriteMutation.mutate({
      resource_type: resourceType,
      resource_id: resourceId,
      is_favorited: effectiveFavorited,
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent the surrounding clickable card/header from also reacting.
    e.stopPropagation();
    toggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    // Native button activation (Enter/Space) already fires onClick — here we
    // only keep the event from bubbling to clickable ancestors.
    if (e.key === "Enter" || e.key === " ") {
      e.stopPropagation();
    }
  };

  const label = effectiveFavorited
    ? "Remover dos favoritos"
    : "Adicionar aos favoritos";

  const testId = testIdPrefix
    ? `${testIdPrefix}-${effectiveFavorited ? "unfavorite" : "favorite"}-button`
    : undefined;

  return (
    <Button
      type="button"
      variant="ghost"
      size="small"
      iconPosition="only"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={label}
      aria-label={label}
      aria-pressed={effectiveFavorited}
      disabled={isPending}
      data-test-id={testId}
      icon={
        <Star
          size={size}
          className={cn(
            "transition-colors duration-150",
            effectiveFavorited
              ? "fill-amber-400 text-amber-500"
              : "fill-none text-muted-foreground",
          )}
          aria-hidden="true"
        />
      }
      className={cn(
        "cursor-pointer",
        "min-w-11 min-h-11 md:min-w-9 md:min-h-9",
        isPending && "opacity-50",
        className,
      )}
    />
  );
}
